import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level error boundary.
 * Catches uncaught render errors and shows a recovery screen
 * instead of a blank white page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // In production, pipe to your error tracker (Sentry, etc.)
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-8 max-w-md w-full text-center">
          {/* Icon */}
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>

          <h1 className="text-xl font-bold font-display text-slate-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed mb-1">
            An unexpected error occurred. Your progress has been saved — you can
            safely refresh or go back to the dashboard.
          </p>

          {/* Error detail (dev only) */}
          {import.meta.env.DEV && (
            <details className="mt-4 text-left">
              <summary className="text-xs font-medium text-slate-400 cursor-pointer hover:text-slate-600">
                Error details
              </summary>
              <pre className="mt-2 text-[11px] text-red-600 bg-red-50 rounded-lg p-3 overflow-auto leading-relaxed whitespace-pre-wrap break-all">
                {error.message}
                {"\n\n"}
                {error.stack}
              </pre>
            </details>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={this.handleReset}
              className="flex-1 rounded-xl border border-slate-200 bg-white text-slate-700 px-4 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Try again
            </button>
            <Link
              to="/dashboard"
              onClick={this.handleReset}
              className="flex-1 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors text-center"
            >
              Go to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
